import { Chip as MuiChip, type SxProps } from "@mui/material";
import type { Theme } from "@mui/material/styles";
import type { SystemStyleObject } from "@mui/system";
import type { MouseEventHandler, ReactElement, ReactNode } from "react";

export type ChipColor =
  | "neutral"
  | "neutral-soft"
  | "neutral-surface"
  | "primary"
  | "success"
  | "warning"
  | "warning-soft"
  | "error"
  | "accent"
  | "accent-soft";

export type ChipVariant = "filled" | "outlined";
export type ChipShape = "rounded" | "pill" | "full";
export type ChipSize = "xsmall" | "small" | "medium";

export type ChipProps = {
  label: ReactNode;
  color?: ChipColor;
  variant?: ChipVariant;
  shape?: ChipShape;
  size?: ChipSize;
  icon?: ReactElement;
  onClick?: MouseEventHandler<HTMLElement>;
  maxWidth?: "none" | "full";
  className?: string;
};

function getMuiColor(color: ChipColor) {
  if (color === "warning-soft") {
    return "warning";
  }

  if (
    color === "primary" ||
    color === "success" ||
    color === "warning" ||
    color === "error"
  ) {
    return color;
  }

  return "default";
}

function getShapeStyles(shape: ChipShape): SystemStyleObject<Theme> {
  if (shape === "pill") {
    return { borderRadius: "var(--radius-xl)" };
  }

  if (shape === "full") {
    return { borderRadius: "9999px" };
  }

  return { borderRadius: "var(--radius-lg)" };
}

function getSizeStyles(size: ChipSize): SystemStyleObject<Theme> {
  if (size === "xsmall") {
    return {
      height: "24px",
      "& .MuiChip-label": {
        paddingInline: "calc(var(--spacing)*2)",
        fontSize: "var(--text-xs)",
        lineHeight: "15px",
        fontWeight: "var(--font-weight-medium)",
      },
    };
  }

  if (size === "small") {
    return {
      height: "24px",
      "& .MuiChip-label": {
        paddingInline: "calc(var(--spacing)*2)",
        fontSize: "var(--text-xs)",
        lineHeight: "var(--text-xs--line-height)",
        fontWeight: "var(--font-weight-medium)",
      },
    };
  }

  return {
    height: "32px",
    "& .MuiChip-label": {
      paddingInline: "calc(var(--spacing)*2)",
      fontSize: "var(--text-sm)",
      lineHeight: "var(--text-sm--line-height)",
    },
  };
}

function getColorVariantStyles(
  color: ChipColor,
  variant: ChipVariant,
  isClickable: boolean,
): SystemStyleObject<Theme> {
  if (color === "accent-soft" && variant === "filled") {
    return {
      backgroundColor: "var(--color-surface-accent-softer)",
      color: "var(--color-text-title)",
    };
  }

  if (color === "accent" && variant === "filled") {
    return {
      backgroundColor: "var(--color-surface-accent-stronger)",
      color: "var(--color-text-inverted)",
    };
  }

  if (color === "warning-soft" && variant === "filled") {
    return {
      backgroundColor: "var(--color-semantic-warning-pressed-soft)",
      color: "var(--color-text-primary)",
    };
  }

  if (color === "neutral-soft" && variant === "filled") {
    return {
      backgroundColor: "var(--color-surface-neutral-soft)",
      color: "var(--color-text-primary)",
    };
  }

  if (color === "neutral-surface" && variant === "filled") {
    return {
      backgroundColor: "var(--color-surface-background)",
      color: "var(--color-text-primary)",
    };
  }

  if (color === "neutral" && variant === "outlined") {
    return {
      backgroundColor: "var(--color-surface-background)",
      borderColor: "var(--color-stroke-neutral-medium)",
      color: "var(--color-text-primary)",
      ...(isClickable
        ? {
            "& .MuiChip-label": {
              paddingInline: "calc(var(--spacing)*4)",
              fontWeight: "var(--font-weight-medium)",
            },
          }
        : {}),
      "&.MuiChip-clickable:hover": {
        backgroundColor: "var(--color-surface-background)",
      },
    };
  }

  return {};
}

export function Chip({
  label,
  color = "neutral-soft",
  variant = "filled",
  shape = "pill",
  size = "small",
  icon,
  onClick,
  maxWidth = "none",
  className,
}: ChipProps) {
  const isClickable = Boolean(onClick);

  const widthStyles: SystemStyleObject<Theme> = {
    maxWidth: maxWidth === "full" ? "100%" : undefined,
  };

  const chipSx: SxProps<Theme> = {
    ...getShapeStyles(shape),
    ...getSizeStyles(size),
    ...getColorVariantStyles(color, variant, isClickable),
    ...widthStyles,
  };

  return (
    <MuiChip
      label={label}
      color={getMuiColor(color)}
      variant={variant}
      size={size === "medium" ? "medium" : "small"}
      icon={icon}
      onClick={onClick}
      className={className}
      sx={chipSx}
    />
  );
}
